import {Button} from 'platform/components';
import {HStack, Show} from 'platform/foundation';

import {isNotNil} from 'ramda';

import {useSourcingGetPresetsListQuery} from '@dms/api/core';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';

import {useBoolean} from 'shared/web';

import {SourcingMobileDialog} from '../../../../components/SourcingMobileDialog/SourcingMobileDialog';
import {useSourcingCurrency} from '../../../../hooks/useSourcingCurrency';
import {useSourcingTargetCountry} from '../../../../hooks/useSourcingTargetCountry';
import {SourcingType, useSourcingType} from '../../../../hooks/useSourcingType';
import {SourcingFilterPresetsControl} from '../../hooks/useSourcingFilterPresets';
import {FilterPresetButton} from './FilterPresetButton';
import {FilterPresetButtons} from './FilterPresetButtons';

export interface SavedFiltersMobileBarProps {
  filterPresetsControl: SourcingFilterPresetsControl;
  isSortingEnabled?: boolean;
}

/**
 * Collapsed phone/tablet entry to the saved filter presets: the applied preset's chip plus the
 * trigger that opens the full list in a dialog. Renders no wrapper of its own - the parent row
 * must use both `wrap` and `justify="space-between"` for the group to sit at the row's end and
 * to drop to a row of its own when it no longer fits.
 */
export function SavedFiltersMobileBar(props: SavedFiltersMobileBarProps) {
  const [isDialogOpen, openDialog, closeDialog] = useBoolean();
  const sourcingType = useSourcingType();
  const targetCountry = useSourcingTargetCountry();
  const currency = useSourcingCurrency();
  const {data: presetsList} = useSourcingGetPresetsListQuery({
    country: targetCountry?.code,
    currency: currency?.code,
  });

  if (props.filterPresetsControl.isEmpty) {
    return null;
  }

  // From the list, not `selectedFilterPreset`: only the list cache is invalidated on rename /
  // mark-all-as-read / notifications toggle, so the detail snapshot would go stale.
  const activeFilterPreset = presetsList?.find(
    (preset) => preset.id === props.filterPresetsControl.selectedFilterPresetId
  );
  const savedFiltersLabel = i18n.t('entity.sourcing.filterPresets.labels.savedFilters');
  const presetCount = presetsList?.length ?? 0;
  // Picking a preset in the dialog also closes it; the bar's own chip only opens the dialog.
  const dialogFilterPresetsControl: SourcingFilterPresetsControl = {
    ...props.filterPresetsControl,
    onSelectFilterPresetId: (id: string) => {
      props.filterPresetsControl.onSelectFilterPresetId(id);
      closeDialog();
    },
  };

  return (
    <>
      <HStack spacing={2} align="center" wrap>
        <Show when={isNotNil(activeFilterPreset)}>
          <FilterPresetButton
            isSelected
            id={activeFilterPreset?.id ?? ''}
            title={activeFilterPreset?.name ?? ''}
            count={
              sourcingType === SourcingType.AUCTIONS ? null : (activeFilterPreset?.newCount ?? null)
            }
            isNotificationsMuted={
              sourcingType === SourcingType.AUCTIONS
                ? null
                : !activeFilterPreset?.allowNotifications
            }
            onClick={openDialog}
            data-testid={testIds.sourcing.classifieds('activeFilterPreset')}
          />
        </Show>
        <Button
          variant="outlined"
          leftIcon="action/bookmarks"
          title={savedFiltersLabel}
          badge={
            presetCount > 0 ? {value: presetCount.toString(), colorScheme: 'neutral'} : undefined
          }
          onClick={openDialog}
          data-testid={testIds.sourcing.classifieds('openSavedFilters')}
        />
      </HStack>
      <SourcingMobileDialog
        isOpen={isDialogOpen}
        onClose={closeDialog}
        title={savedFiltersLabel}
        data-testid={testIds.sourcing.classifieds('savedFiltersDialog')}
      >
        <FilterPresetButtons
          filterPresetsControl={dialogFilterPresetsControl}
          isSortingEnabled={props.isSortingEnabled}
        />
      </SourcingMobileDialog>
    </>
  );
}
